import { AppLanguage, getPreferredLanguage } from '../language';

interface TeamMemberText {
  name: string;
  role: string;
  intro: string;
}

interface MilestoneItemText {
  date: string;
  title: string;
  content: string;
}

interface MilestoneYearText {
  year: string;
  items: MilestoneItemText[];
}

export interface AboutI18nTemplate {
  ui: {
    heroBadge: string;
    heroTitlePrefix: string;
    heroTitleAccent: string;
    heroDescription: string;
    sectionAboutTitle: string;
    aboutParagraph1: string;
    aboutParagraph2: string;
    aboutParagraph3: string;
    badgeVerified: string;
    badgeGlobalNetwork: string;
    badgeNoCommission: string;
    mediaPlaceholderTitle: string;
    mediaPlaceholderSubtitle: string;
    sectionTeamTitle: string;
    sectionMilestonesTitle: string;
    sectionContactTitle: string;
    sectionContactDescription: string;
    actionXiaohongshu: string;
    actionAddWechat: string;
    assistantName: string;
  };
  teamMembers: TeamMemberText[];
  milestones: MilestoneYearText[];
}

export const TEMPLATES: Record<AppLanguage, AboutI18nTemplate> = {
  zh: {
    ui: {
      heroBadge: '做跨境，找海海',
      heroTitlePrefix: '跨境卖家的',
      heroTitleAccent: '技术搭档',
      heroDescription: '我们是一群在硅谷工作的工程师。因为看过了太多跨境路上的“坑”，我们决定用最纯粹的技术，把物流变清楚。',
      sectionAboutTitle: '关于海海',
      aboutParagraph1: '海海（SSEEAA）诞生于一个简单的想法：为什么中小卖家不能像大厂一样，拥有透明、高效的供应链工具？',
      aboutParagraph2: '我们团队成员曾任职于 Google, Meta 等硅谷科技巨头。我们深知技术能带来的改变。于是，我们把复杂的阶梯报价、偏远地区附加费、燃油调整等，全部封装进后台算法中。',
      aboutParagraph3: '我们始终站在卖家这一边。作为你们的技术部门，我们不仅提供工具，更代表你们去审查服务商、死磕价格。',
      badgeVerified: '100% 验真',
      badgeGlobalNetwork: '全球网络',
      badgeNoCommission: '零佣金推荐',
      mediaPlaceholderTitle: '介绍视频/图集 (预留)',
      mediaPlaceholderSubtitle: 'Video or image gallery position',
      sectionTeamTitle: '我们的团队',
      sectionMilestonesTitle: '发展历程',
      sectionContactTitle: '随时联络我们',
      sectionContactDescription: '无论是对物流的困惑，还是对海海的建议，海海的小助手随时准备为你提供建议。',
      actionXiaohongshu: '海海小红书',
      actionAddWechat: '添加企业微信',
      assistantName: '海海小助手萱萱',
    },
    teamMembers: [
      {
        name: '佳澔',
        role: '联合创始人',
        intro: '硅谷大厂工程师。坚信代码应该为我们在乎的人而写，为中小卖家提供大厂级别的技术支持。',
      },
      {
        name: '一坤',
        role: '联合创始人',
        intro: '连续创业者。始终站在业务一线，帮卖家在复杂的海外市场中寻找最务实的增长路径。',
      },
      {
        name: '一凡',
        role: '创始团队成员',
        intro: '架构师。负责海海底层系统的稳定与效率，致力于让每一次物流试算都能在毫秒级完成。',
      },
      {
        name: '邱昊',
        role: '创始团队成员',
        intro: '前端专家。追求极致的用户体验，要把冷冰冰的物流工具打造得像艺术品一样优雅易用。',
      },
      {
        name: '陈蕾',
        role: '创始团队成员',
        intro: '数据科学家。通过动态数据建模分析，为卖家筛选出性价比最高的物流组合。',
      },
      {
        name: '昕澔',
        role: '创始团队成员',
        intro: 'AI 工程师。将最前沿的 AI 技术融入跨境场景，打造 24 小时在线的智能物流助手。',
      },
      {
        name: 'Lisa',
        role: '创始团队成员',
        intro: '商业地产经纪。利用十余年本地经验，为海海卖家对接最靠谱的实地仓储资源。',
      },
    ],
    milestones: [
      {
        year: '2025',
        items: [
          { date: '04.20', title: '海海 1.0 上线', content: '跨境卖家可以通过海海在 30 秒内获得全球海外仓的精确报价。' },
          { date: '02.27', title: '深度合作达成', content: '与美西、美东核心仓库达成深度合作，开启海外仓信息化升级计划。' },
          { date: '01.12', title: '美国工厂启动', content: '首批上线 POD 项目，支持海外本地化定制生产方案。' },
        ],
      },
      {
        year: '2024',
        items: [
          { date: '12.14', title: 'WMS 深度集成', content: '与行业排名前 3 的 WMS 厂商达成合作，创新软件计费模式。' },
          { date: '11.02', title: '第一场实地访谈', content: '在洛杉矶完成首家海外仓实地验真，确立了“真实可信”的筛选标准。' },
          { date: '10.15', title: '缘起小红书', content: '团队成员因对“解决跨境乱象”的共鸣在小红书集结，海海正式启航。' },
        ],
      },
    ],
  },
  en: {
    ui: {
      heroBadge: 'Cross-border, with SSEEAA',
      heroTitlePrefix: 'A technical partner for ',
      heroTitleAccent: 'cross-border sellers',
      heroDescription: 'We are engineers working in Silicon Valley. After seeing too many pitfalls in cross-border logistics, we decided to make it clear with pure technology.',
      sectionAboutTitle: 'About SSEEAA',
      aboutParagraph1: 'SSEEAA started from a simple question: why can’t small and medium sellers have transparent, efficient supply-chain tools like large companies?',
      aboutParagraph2: 'Our team previously worked at Silicon Valley tech companies like Google and Meta. We know what technology can change, so we encoded complex ladder pricing, remote surcharges, and fuel adjustments into backend algorithms.',
      aboutParagraph3: 'We stay on the seller’s side. As your technical team, we provide tools and also help you vet providers and negotiate for better pricing.',
      badgeVerified: '100% verified',
      badgeGlobalNetwork: 'Global network',
      badgeNoCommission: 'Zero-commission recommendation',
      mediaPlaceholderTitle: 'Intro video/gallery (reserved)',
      mediaPlaceholderSubtitle: 'Video or image gallery position',
      sectionTeamTitle: 'Our team',
      sectionMilestonesTitle: 'Milestones',
      sectionContactTitle: 'Contact us anytime',
      sectionContactDescription: 'Whether you have logistics questions or product feedback, our assistant is ready to help.',
      actionXiaohongshu: 'SSEEAA Xiaohongshu',
      actionAddWechat: 'Add WeCom',
      assistantName: 'Assistant Xuanxuan',
    },
    teamMembers: [
      {
        name: 'Jiahao',
        role: 'Co-founder',
        intro: 'Engineer from major Silicon Valley companies. Believes code should serve people we care about and bring top-tier support to SMB sellers.',
      },
      {
        name: 'Yikun',
        role: 'Co-founder',
        intro: 'Serial entrepreneur focused on frontline operations, helping sellers find pragmatic growth paths in complex overseas markets.',
      },
      {
        name: 'Yifan',
        role: 'Founding team member',
        intro: 'System architect responsible for platform stability and speed, aiming for millisecond-level quote calculations.',
      },
      {
        name: 'Qiu Hao',
        role: 'Founding team member',
        intro: 'Frontend specialist focused on turning complex logistics workflows into elegant, usable product experiences.',
      },
      {
        name: 'Chen Lei',
        role: 'Founding team member',
        intro: 'Data scientist who uses dynamic modeling to identify the best value logistics combinations.',
      },
      {
        name: 'Xinhao',
        role: 'Founding team member',
        intro: 'AI engineer integrating cutting-edge AI into cross-border workflows to build a 24/7 intelligent logistics assistant.',
      },
      {
        name: 'Lisa',
        role: 'Founding team member',
        intro: 'Commercial real estate broker with over a decade of local experience connecting sellers with reliable warehousing resources.',
      },
    ],
    milestones: [
      {
        year: '2025',
        items: [
          { date: '04.20', title: 'SSEEAA 1.0 launched', content: 'Cross-border sellers can get accurate overseas warehouse quotes in 30 seconds.' },
          { date: '02.27', title: 'Strategic partnerships', content: 'Deep partnerships established with core West and East US warehouses.' },
          { date: '01.12', title: 'US factory launched', content: 'First POD projects went live for localized production support.' },
        ],
      },
      {
        year: '2024',
        items: [
          { date: '12.14', title: 'Deep WMS integration', content: 'Partnered with top-3 WMS providers and introduced innovative software pricing.' },
          { date: '11.02', title: 'First on-site visits', content: 'Completed first on-site warehouse validation in Los Angeles, defining our verification standards.' },
          { date: '10.15', title: 'Origin on Xiaohongshu', content: 'The team assembled around a shared goal to fix chaos in cross-border logistics.' },
        ],
      },
    ],
  },
};

export const ABOUT_NAMESPACE = 'about';

export const ABOUT_RESOURCE_KEY = 'template';

export const ABOUT_I18N_RESOURCES: Record<
  AppLanguage,
  Record<typeof ABOUT_RESOURCE_KEY, AboutI18nTemplate>
> = {
  zh: {
    [ABOUT_RESOURCE_KEY]: TEMPLATES.zh,
  },
  en: {
    [ABOUT_RESOURCE_KEY]: TEMPLATES.en,
  },
};

export const getAboutTemplate = (
  language: AppLanguage = getPreferredLanguage(),
): AboutI18nTemplate => TEMPLATES[language] || TEMPLATES.zh;
